import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Coins, UserPlus, AlertCircle, GraduationCap, Users, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('student');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const result = await register(name, email, password, role);
      
      // Check if professor needs approval
      if (result.user?.status === 'pending') {
        setPendingApproval(true);
        toast.info('Tu cuenta está pendiente de aprobación');
      } else {
        toast.success('¡Cuenta creada exitosamente!');
        const pendingJoin = sessionStorage.getItem('pendingJoin');
        if (pendingJoin) {
          sessionStorage.removeItem('pendingJoin');
          navigate(`/join/${pendingJoin}`);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      let message;
      if (!err.response) {
        message = 'No se pudo conectar al servidor. Verifica tu conexión a internet.';
      } else {
        message = err.response.data?.detail || 'Error al crear cuenta. Intenta de nuevo.';
      }
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Show pending approval screen
  if (pendingApproval) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          <Card className="border-slate-200 shadow-xl shadow-slate-200/50">
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Registro Exitoso</h2>
              <p className="text-slate-500 mb-6">
                Tu cuenta de profesor está pendiente de aprobación por el administrador. 
                Recibirás un correo cuando sea aprobada.
              </p>
              <Link to="/login">
                <Button className="w-full" data-testid="go-login-btn">
                  Ir a Iniciar Sesión
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 shadow-lg shadow-amber-500/30 mb-4">
            <Coins className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold font-heading text-slate-900">CampusCoins</h1>
          <p className="text-slate-500 mt-1">Sistema de Gamificación Universitaria</p>
        </div>

        <Card className="border-slate-200 shadow-xl shadow-slate-200/50">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-heading">Crear Cuenta</CardTitle>
            <CardDescription>
              Completa tus datos para registrarte
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              {/* Role Selection */}
              <div className="space-y-3">
                <Label>Tipo de Usuario</Label>
                <RadioGroup 
                  value={role} 
                  onValueChange={setRole}
                  className="grid grid-cols-2 gap-3"
                >
                  <div>
                    <RadioGroupItem 
                      value="student" 
                      id="student" 
                      className="peer sr-only" 
                    />
                    <Label
                      htmlFor="student"
                      className="flex flex-col items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 hover:bg-slate-50"
                      data-testid="role-student"
                    >
                      <Users className="w-6 h-6 mb-2 text-blue-600" />
                      <span className="font-medium text-slate-900">Estudiante</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem 
                      value="professor" 
                      id="professor" 
                      className="peer sr-only" 
                    />
                    <Label
                      htmlFor="professor"
                      className="flex flex-col items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 hover:bg-slate-50"
                      data-testid="role-professor"
                    >
                      <GraduationCap className="w-6 h-6 mb-2 text-blue-600" />
                      <span className="font-medium text-slate-900">Profesor</span>
                    </Label>
                  </div>
                </RadioGroup>
                {role === 'professor' && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    Las cuentas de profesor requieren aprobación del administrador
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nombre Completo</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Juan Pérez"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-11 bg-slate-50 focus:bg-white"
                  data-testid="register-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@universidad.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 bg-slate-50 focus:bg-white"
                  data-testid="register-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 bg-slate-50 focus:bg-white"
                  data-testid="register-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repite tu contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11 bg-slate-50 focus:bg-white"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col gap-4">
              <Button 
                type="submit" 
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creando cuenta...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Crear Cuenta
                  </span>
                )}
              </Button>
              
              <p className="text-sm text-slate-500 text-center">
                ¿Ya tienes cuenta?{' '}
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="login-link"
                >
                  Inicia sesión
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
